// src/lib/whatsappService.js

/**
 * Generate WhatsApp share link
 * @param {string} phoneNumber - Recipient phone number
 * @param {string} message - Message to send
 * @returns {string} WhatsApp share URL
 */
export const generateWhatsAppLink = (phoneNumber, message) => {
  // Remove all non-digit characters from phone number
  const cleanNumber = phoneNumber.toString().replace(/\D/g, '')
  const encodedMessage = encodeURIComponent(message)
  return `https://wa.me/${cleanNumber}?text=${encodedMessage}`
}

/**
 * Generate property message
 * @param {object} property - Property data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generatePropertyMessage = (property, companyName = 'EstatesFlow') => {
  return `🏠 *${companyName} - New Property Listing*\n\n` +
    `*Title:* ${property.title || 'N/A'}\n` +
    `*Price:* ${property.currency || 'USD'} ${property.price?.toLocaleString() || 'N/A'}\n` +
    `*Location:* ${property.country || 'N/A'}, ${property.city || 'N/A'}\n` +
    `*Bedrooms:* ${property.bedrooms || 'N/A'}\n` +
    `*Bathrooms:* ${property.bathrooms || 'N/A'}\n` +
    (property.description ? `*Description:* ${property.description.substring(0, 100)}...\n\n` : '\n') +
    `View more: ${window.location.origin}/properties/${property.id}`
}

/**
 * Generate lead message
 * @param {object} lead - Lead data
 * @param {string} status - Lead status
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateLeadMessage = (lead, status, companyName = 'EstatesFlow') => {
  const statusEmoji = { 
    new: '🟡', 
    contacted: '📞', 
    negotiating: '🤝', 
    closed: '✅' 
  }
  const statusText = status || lead.status || 'new'
  
  return `🎯 *${companyName} - Lead Update*\n\n` +
    `*Client:* ${lead.client_name || 'N/A'}\n` +
    `*Status:* ${statusEmoji[statusText] || '📌'} ${statusText}\n` +
    `*Score:* ${lead.score || 10} pts\n` +
    `*Email:* ${lead.client_email || 'N/A'}\n` +
    `*Phone:* ${lead.client_phone || 'N/A'}\n\n` +
    `Let's follow up on this lead!`
}

/**
 * Generate appointment message
 * @param {object} appointment - Appointment data
 * @param {object} client - Client data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateAppointmentMessage = (appointment, client, companyName = 'EstatesFlow') => {
  return `📅 *${companyName} - Appointment Reminder*\n\n` +
    `*Client:* ${client?.full_name || 'Valued Client'}\n` +
    `*Date:* ${new Date(appointment.date_time).toLocaleString()}\n` +
    `*Location:* ${appointment.location || 'To be confirmed'}\n` +
    `*Notes:* ${appointment.notes || 'None'}\n\n` +
    `Please confirm your attendance.`
}

/**
 * Generate client message
 * @param {object} client - Client data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateClientMessage = (client, companyName = 'EstatesFlow') => {
  return `👤 *${companyName} - Client Profile*\n\n` +
    `*Name:* ${client.full_name || 'N/A'}\n` +
    `*Email:* ${client.email || 'N/A'}\n` +
    `*Phone:* ${client.phone || 'N/A'}\n` +
    `*Type:* ${client.client_type || 'N/A'}\n` +
    (client.address ? `*Address:* ${client.address}\n` : '') +
    `\nNeed assistance? Reply to this message.`
}

/**
 * Generate invoice message
 * @param {object} invoice - Invoice data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateInvoiceMessage = (invoice, companyName = 'EstatesFlow') => {
  return `💰 *${companyName} - Invoice Reminder*\n\n` +
    `*Invoice #:* ${invoice.invoice_number || 'N/A'}\n` +
    `*Amount:* ${invoice.currency || 'USD'} ${invoice.total?.toLocaleString() || 'N/A'}\n` +
    `*Due Date:* ${new Date(invoice.due_date).toLocaleDateString()}\n` +
    `*Status:* ${invoice.status || 'pending'}\n\n` +
    `Thank you for your prompt payment.`
}

/**
 * Generate contract message
 * @param {object} contract - Contract data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateContractMessage = (contract, companyName = 'EstatesFlow') => {
  return `⚖️ *${companyName} - Contract Reminder*\n\n` +
    `*Contract:* ${contract.title || 'N/A'}\n` +
    `*Type:* ${contract.type || 'N/A'}\n` +
    `*Value:* ${contract.currency || 'USD'} ${contract.value?.toLocaleString() || 'N/A'}\n` +
    (contract.start_date ? `*Start Date:* ${new Date(contract.start_date).toLocaleDateString()}\n` : '') +
    (contract.end_date ? `*Expires:* ${new Date(contract.end_date).toLocaleDateString()}\n` : '') +
    `\nPlease review and sign at your earliest convenience.`
}

/**
 * Generate interview message
 * @param {object} interview - Interview data
 * @param {object} job - Job data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateInterviewMessage = (interview, job, companyName = 'EstatesFlow') => {
  return `🎯 *${companyName} - Interview Invitation*\n\n` +
    `*Position:* ${job?.title || 'the position'}\n` +
    `*Type:* ${interview.interview_type || 'Standard'}\n` +
    `*Date:* ${new Date(interview.scheduled_date).toLocaleString()}\n` +
    `*Location:* ${interview.location || interview.meeting_link || 'TBD'}\n\n` +
    `Please confirm your availability.`
}

/**
 * Generate job offer message
 * @param {object} offer - Offer data
 * @param {object} job - Job data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateOfferMessage = (offer, job, companyName = 'EstatesFlow') => {
  return `📄 *${companyName} - Job Offer*\n\n` +
    `*Position:* ${job?.title || 'N/A'}\n` +
    `*Salary:* ${offer.salary_currency || 'USD'} ${offer.salary_offered?.toLocaleString() || 'N/A'}\n` +
    `*Start Date:* ${new Date(offer.start_date).toLocaleDateString()}\n\n` +
    `Congratulations! We're excited to have you join our team.`
}

/**
 * Generate complaint message
 * @param {object} complaint - Complaint data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateComplaintMessage = (complaint, companyName = 'EstatesFlow') => {
  return `⚠️ *${companyName} - Complaint Update*\n\n` +
    `*Type:* ${complaint.complaint_type || 'General'}\n` +
    `*Status:* ${complaint.status || 'open'}\n` +
    `*Priority:* ${complaint.priority || 'medium'}\n` +
    (complaint.description ? `*Issue:* ${complaint.description.substring(0, 150)}...\n` : '') +
    `\nWe're working on resolving your issue. Thank you for your patience.`
}

/**
 * Generate feedback message
 * @param {object} feedback - Feedback data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateFeedbackMessage = (feedback, companyName = 'EstatesFlow') => {
  return `⭐ *${companyName} - Thank you for your feedback!*\n\n` +
    `*Rating:* ${'⭐'.repeat(feedback.rating)} (${feedback.rating}/5)\n` +
    `*Category:* ${feedback.category || 'General'}\n` +
    `*Feedback:* "${feedback.feedback || 'No comment'}"\n\n` +
    `We appreciate your input and will use it to improve our services.`
}

/**
 * Generate salary message
 * @param {object} salary - Salary data
 * @param {object} agent - Agent data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateSalaryMessage = (salary, agent, companyName = 'EstatesFlow') => {
  return `💰 *${companyName} - Salary Advice*\n\n` +
    `*Employee:* ${agent?.email || 'N/A'}\n` +
    `*Period:* ${salary.month}/${salary.year}\n` +
    `*Amount:* ${salary.currency || 'USD'} ${salary.net_salary?.toLocaleString() || 'N/A'}\n` +
    `*Status:* ${salary.status || 'pending'}\n\n` +
    `Please check your account for details.`
}

/**
 * Generate tax return message
 * @param {object} taxReturn - Tax return data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateTaxReturnMessage = (taxReturn, companyName = 'EstatesFlow') => {
  return `🏛️ *${companyName} - Tax Return*\n\n` +
    `*Type:* ${taxReturn.tax_type || 'N/A'}\n` +
    `*Period:* ${taxReturn.period || 'N/A'}\n` +
    `*Amount Due:* ${taxReturn.currency || 'USD'} ${taxReturn.amount_due?.toLocaleString() || 'N/A'}\n` +
    `*Due Date:* ${new Date(taxReturn.due_date).toLocaleDateString()}\n\n` +
    `Please ensure timely payment to avoid penalties.`
}

/**
 * Generate promotional message (bulk)
 * @param {string} message - Custom message
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateBulkMessage = (message, companyName = 'EstatesFlow') => {
  if (message) return message
  return `🏢 *${companyName}*\n\n` +
    `Thank you for being a valued customer!\n\n` +
    `Visit our portal for more information.\n\n` +
    `${window.location.origin}`
}

/**
 * Generate document share message
 * @param {object} document - Document data
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateDocumentMessage = (document, companyName = 'EstatesFlow') => {
  return `📄 *${companyName} - Document Shared*\n\n` +
    `*Title:* ${document.title || 'N/A'}\n` +
    `*Type:* ${document.type || 'General'}\n` +
    `*Uploaded:* ${new Date(document.created_at).toLocaleDateString()}\n\n` +
    `View document: ${window.location.origin}/documents/${document.id}`
}

/**
 * Generic welcome message
 * @param {string} clientName - Client name
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const generateWelcomeMessage = (clientName, companyName = 'EstatesFlow') => {
  return `👋 *${companyName} - Welcome!*\n\n` +
    `Dear ${clientName || 'Valued Customer'},\n\n` +
    `Welcome to ${companyName}! We're excited to have you on board.\n\n` +
    `Feel free to reach out if you have any questions.\n\n` +
    `Best regards,\n` +
    `The ${companyName} Team`
}

/**
 * Main function to get appropriate message based on type
 * @param {string} type - Message type (property, lead, client, etc.)
 * @param {object} data - Data object
 * @param {string} companyName - Company name (default: 'EstatesFlow')
 * @returns {string} Formatted message
 */
export const getWhatsAppMessage = (type, data, companyName = 'EstatesFlow') => {
  switch (type) {
    case 'property':
      return generatePropertyMessage(data, companyName)
    case 'lead':
      return generateLeadMessage(data, data?.status, companyName)
    case 'appointment':
      return generateAppointmentMessage(data, data?.client, companyName)
    case 'client':
      return generateClientMessage(data, companyName)
    case 'invoice':
      return generateInvoiceMessage(data, companyName)
    case 'contract':
      return generateContractMessage(data, companyName)
    case 'interview':
      return generateInterviewMessage(data, data?.job, companyName)
    case 'offer':
      return generateOfferMessage(data, data?.job_postings, companyName)
    case 'complaint':
      return generateComplaintMessage(data, companyName)
    case 'feedback':
      return generateFeedbackMessage(data, companyName)
    case 'salary':
      return generateSalaryMessage(data, data?.agent, companyName)
    case 'tax':
      return generateTaxReturnMessage(data, companyName)
    case 'document':
      return generateDocumentMessage(data, companyName)
    case 'welcome':
      return generateWelcomeMessage(data?.name, companyName)
    case 'bulk':
      return generateBulkMessage(data?.message, companyName)
    default:
      return generateBulkMessage(null, companyName)
  }
}

export default {
  generateWhatsAppLink,
  generatePropertyMessage,
  generateLeadMessage,
  generateAppointmentMessage,
  generateClientMessage,
  generateInvoiceMessage,
  generateContractMessage,
  generateInterviewMessage,
  generateOfferMessage,
  generateComplaintMessage,
  generateFeedbackMessage,
  generateSalaryMessage,
  generateTaxReturnMessage,
  generateDocumentMessage,
  generateWelcomeMessage,
  generateBulkMessage,
  getWhatsAppMessage
}